import {
  ArrowPathIcon,
  CheckCircleIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import AdminApiService from '../../utils/api';

const BrandsModelsSettings = ({ open, onClose }) => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingBrand, setSavingBrand] = useState(false);
  const [savingModel, setSavingModel] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [newBrandName, setNewBrandName] = useState('');
  const [newModelName, setNewModelName] = useState('');
  const [search, setSearch] = useState('');
  const [editingBrandId, setEditingBrandId] = useState(null);
  const [editingBrandValue, setEditingBrandValue] = useState('');
  const [editingModelKey, setEditingModelKey] = useState(null);
  const [editingModelValue, setEditingModelValue] = useState('');

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const response = await AdminApiService.getCarBrands();
      const data = Array.isArray(response?.data) ? response.data : [];
      setBrands(data);
      if (data.length > 0 && !data.find((b) => String(b._id) === String(selectedBrandId))) {
        setSelectedBrandId(String(data[0]._id));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load brands');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchBrands();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filteredBrands = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return brands;
    return brands.filter((brand) => brand.name.toLowerCase().includes(term));
  }, [brands, search]);

  const selectedBrand = useMemo(
    () => brands.find((brand) => String(brand._id) === String(selectedBrandId)) || null,
    [brands, selectedBrandId]
  );

  const handleAddBrand = async () => {
    const value = newBrandName.trim();
    if (!value) return;
    setSavingBrand(true);
    try {
      const response = await AdminApiService.createCarBrand({ name: value });
      const created = response?.data;
      toast.success(`Added brand "${value}"`);
      setNewBrandName('');
      await fetchBrands();
      if (created?._id) {
        setSelectedBrandId(String(created._id));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to add brand');
    } finally {
      setSavingBrand(false);
    }
  };

  const handleStartEditBrand = (brand) => {
    setEditingBrandId(String(brand._id));
    setEditingBrandValue(brand.name);
  };

  const handleSaveBrand = async (brand) => {
    const value = editingBrandValue.trim();
    if (!value || value === brand.name) {
      setEditingBrandId(null);
      return;
    }
    try {
      await AdminApiService.updateCarBrand(brand._id, { name: value });
      toast.success('Brand renamed');
      setEditingBrandId(null);
      fetchBrands();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to rename brand');
    }
  };

  const handleToggleBrandActive = async (brand) => {
    try {
      await AdminApiService.updateCarBrand(brand._id, { isActive: !brand.isActive });
      toast.success(brand.isActive ? 'Brand hidden' : 'Brand activated');
      fetchBrands();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update brand');
    }
  };

  const handleDeleteBrand = async (brand) => {
    if (
      !window.confirm(
        `Delete "${brand.name}" and all its models?\n\nExisting cars using this brand are not affected — only future selections in the seller / agent forms will be blocked.`
      )
    ) {
      return;
    }
    try {
      await AdminApiService.deleteCarBrand(brand._id);
      toast.success('Brand deleted');
      if (String(selectedBrandId) === String(brand._id)) {
        setSelectedBrandId(null);
      }
      fetchBrands();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete brand');
    }
  };

  const handleAddModel = async () => {
    if (!selectedBrand) return;
    const value = newModelName.trim();
    if (!value) return;
    setSavingModel(true);
    try {
      await AdminApiService.addCarModel(selectedBrand._id, { name: value });
      toast.success(`Added model "${value}" to ${selectedBrand.name}`);
      setNewModelName('');
      fetchBrands();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to add model');
    } finally {
      setSavingModel(false);
    }
  };

  const handleStartEditModel = (brand, model) => {
    setEditingModelKey(`${brand._id}:${model._id}`);
    setEditingModelValue(model.name);
  };

  const handleSaveModel = async (brand, model) => {
    const value = editingModelValue.trim();
    if (!value || value === model.name) {
      setEditingModelKey(null);
      return;
    }
    try {
      await AdminApiService.updateCarModel(brand._id, model._id, { name: value });
      toast.success('Model renamed');
      setEditingModelKey(null);
      fetchBrands();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to rename model');
    }
  };

  const handleToggleModelActive = async (brand, model) => {
    try {
      await AdminApiService.updateCarModel(brand._id, model._id, { isActive: !model.isActive });
      toast.success(model.isActive ? 'Model hidden' : 'Model activated');
      fetchBrands();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update model');
    }
  };

  const handleDeleteModel = async (brand, model) => {
    if (!window.confirm(`Delete model "${model.name}" from "${brand.name}"?`)) return;
    try {
      await AdminApiService.deleteCarModel(brand._id, model._id);
      toast.success('Model deleted');
      fetchBrands();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete model');
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Car brands &amp; models</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              These appear as dropdowns in the seller app and agent dashboard car forms. Brand has
              to be picked first; the model dropdown only unlocks after that.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center flex-1 p-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 overflow-hidden">
            {/* Brands column */}
            <div className="border-r border-gray-200 flex flex-col overflow-hidden">
              <div className="p-4 border-b border-gray-100 space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Brands ({brands.length})
                  </h3>
                  <button
                    type="button"
                    onClick={fetchBrands}
                    className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                    title="Refresh"
                  >
                    <ArrowPathIcon className="w-4 h-4" />
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Search brands..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Add brand (e.g. Citroen)"
                    value={newBrandName}
                    onChange={(e) => setNewBrandName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddBrand();
                      }
                    }}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddBrand}
                    disabled={savingBrand || !newBrandName.trim()}
                    className="inline-flex items-center px-3 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg"
                  >
                    <PlusIcon className="w-4 h-4 mr-1" />
                    Add
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                {filteredBrands.length === 0 && (
                  <p className="p-8 text-center text-sm text-gray-500">No brands found.</p>
                )}
                {filteredBrands.map((brand) => {
                  const isSelected = String(brand._id) === String(selectedBrandId);
                  const editing = editingBrandId === String(brand._id);
                  return (
                    <div
                      key={brand._id}
                      className={`p-3 cursor-pointer transition-colors ${
                        isSelected ? 'bg-primary-50' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => !editing && setSelectedBrandId(String(brand._id))}
                    >
                      <div className="flex items-center justify-between gap-2">
                        {editing ? (
                          <input
                            autoFocus
                            value={editingBrandValue}
                            onChange={(e) => setEditingBrandValue(e.target.value)}
                            onBlur={() => handleSaveBrand(brand)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSaveBrand(brand);
                              } else if (e.key === 'Escape') {
                                setEditingBrandId(null);
                              }
                            }}
                            className="flex-1 px-2 py-1 text-sm border border-primary-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                        ) : (
                          <div className="flex-1 min-w-0">
                            <p
                              className={`font-medium truncate ${
                                brand.isActive ? 'text-gray-900' : 'text-gray-400 line-through'
                              }`}
                            >
                              {brand.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {(brand.models || []).filter((m) => m.isActive).length} active /{' '}
                              {(brand.models || []).length} total models
                            </p>
                          </div>
                        )}

                        <div className="flex items-center gap-1 opacity-70 group-hover:opacity-100">
                          {!editing && (
                            <>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStartEditBrand(brand);
                                }}
                                className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                                title="Rename"
                              >
                                <PencilSquareIcon className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleBrandActive(brand);
                                }}
                                className="p-1.5 text-gray-400 hover:text-warning-600 hover:bg-warning-50 rounded"
                                title={brand.isActive ? 'Hide brand' : 'Show brand'}
                              >
                                {brand.isActive ? (
                                  <CheckCircleIcon className="w-4 h-4 text-success-600" />
                                ) : (
                                  <XCircleIcon className="w-4 h-4 text-gray-400" />
                                )}
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteBrand(brand);
                                }}
                                className="p-1.5 text-gray-400 hover:text-danger-600 hover:bg-danger-50 rounded"
                                title="Delete brand"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Models column */}
            <div className="flex flex-col overflow-hidden">
              <div className="p-4 border-b border-gray-100 space-y-2">
                <h3 className="text-sm font-semibold text-gray-900">
                  {selectedBrand
                    ? `${selectedBrand.name} models (${(selectedBrand.models || []).length})`
                    : 'Pick a brand on the left'}
                </h3>
                {selectedBrand && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder={`Add model to ${selectedBrand.name}`}
                      value={newModelName}
                      onChange={(e) => setNewModelName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddModel();
                        }
                      }}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <button
                      type="button"
                      onClick={handleAddModel}
                      disabled={savingModel || !newModelName.trim()}
                      className="inline-flex items-center px-3 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg"
                    >
                      <PlusIcon className="w-4 h-4 mr-1" />
                      Add
                    </button>
                  </div>
                )}
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                {!selectedBrand && (
                  <p className="p-8 text-center text-sm text-gray-500">
                    Select a brand to manage its models.
                  </p>
                )}
                {selectedBrand && (selectedBrand.models || []).length === 0 && (
                  <p className="p-8 text-center text-sm text-gray-500">
                    No models yet — add one above.
                  </p>
                )}
                {selectedBrand &&
                  (selectedBrand.models || []).map((model) => {
                    const editKey = `${selectedBrand._id}:${model._id}`;
                    const editing = editingModelKey === editKey;
                    return (
                      <div key={editKey} className="p-3 flex items-center gap-2 hover:bg-gray-50">
                        {editing ? (
                          <input
                            autoFocus
                            value={editingModelValue}
                            onChange={(e) => setEditingModelValue(e.target.value)}
                            onBlur={() => handleSaveModel(selectedBrand, model)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSaveModel(selectedBrand, model);
                              } else if (e.key === 'Escape') {
                                setEditingModelKey(null);
                              }
                            }}
                            className="flex-1 px-2 py-1 text-sm border border-primary-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                        ) : (
                          <p
                            className={`flex-1 ${
                              model.isActive ? 'text-gray-900' : 'text-gray-400 line-through'
                            }`}
                          >
                            {model.name}
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={() => handleStartEditModel(selectedBrand, model)}
                          className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                          title="Rename"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleToggleModelActive(selectedBrand, model)}
                          className="p-1.5 text-gray-400 hover:text-warning-600 hover:bg-warning-50 rounded"
                          title={model.isActive ? 'Hide model' : 'Show model'}
                        >
                          {model.isActive ? (
                            <CheckCircleIcon className="w-4 h-4 text-success-600" />
                          ) : (
                            <XCircleIcon className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteModel(selectedBrand, model)}
                          className="p-1.5 text-gray-400 hover:text-danger-600 hover:bg-danger-50 rounded"
                          title="Delete model"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

        <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Done
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default BrandsModelsSettings;
